<template>
  <div class="workorder-detail">
    <!-- 头部工具栏 -->
    <div class="detail-header">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ path: '/workorders' }">工单列表</el-breadcrumb-item>
        <el-breadcrumb-item>{{ workorder.order_number }}</el-breadcrumb-item>
      </el-breadcrumb>

      <div class="header-actions">
        <el-button @click="refreshData" :loading="loading">刷新</el-button>
        <el-button type="primary" @click="handleEdit" v-if="permission.can_edit">编辑</el-button>
        <el-button type="danger" @click="handleDelete" v-if="permission.can_delete">删除</el-button>
      </div>
    </div>

    <!-- 工单基本信息 -->
    <el-card class="info-card">
      <template #header>
        <div class="card-header">
          <span>工单信息</span>
          <el-tag :type="statusType" size="small">{{ workorder.status }}</el-tag>
        </div>
      </template>

      <el-descriptions :column="2" border>
        <el-descriptions-item label="工单号">{{ workorder.order_number }}</el-descriptions-item>
        <el-descriptions-item label="标题">{{ workorder.title }}</el-descriptions-item>
        <el-descriptions-item label="环境">{{ workorder.environment }}</el-descriptions-item>
        <el-descriptions-item label="租户版本">{{ workorder.tenant_version }}</el-descriptions-item>
        <el-descriptions-item label="租户ID">{{ workorder.tenant_id }}</el-descriptions-item>
        <el-descriptions-item label="租户名称">{{ workorder.tenant_name }}</el-descriptions-item>
        <el-descriptions-item label="部署环境">{{
            getDeploymentEnvironmentName(workorder.deployment_environment_id)
          }}
        </el-descriptions-item>
        <el-descriptions-item label="手机类型">{{ workorder.work_phone_type }}</el-descriptions-item>
        <el-descriptions-item label="手机版本">{{ workorder.work_phone_version }}</el-descriptions-item>
        <el-descriptions-item label="设备IMEI">{{ workorder.work_phone_imei }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDate(workorder.created_at) }}</el-descriptions-item>
        <el-descriptions-item label="耗费时间">{{ formatTimeSpent(workorder.time_spent) }}</el-descriptions-item>
        <el-descriptions-item label="创建人">{{ workorder.creator?.username }}</el-descriptions-item>
        <!-- 修复当前处理人编辑问题 -->
        <el-descriptions-item label="当前处理人">
          <div style="display: flex; align-items: center; gap: 10px;">
            <el-select
                v-model="currentHandlerId"
                size="small"
                style="min-width: 150px;"
                @change="handleAssigneeChange"
                :disabled="!permission.can_assign"
                filterable
                remote
                :remote-method="debouncedLoadAssignUsers"
                @focus="loadAssignUsers('')"
            >
              <el-option
                  v-if="workorder.current_handler"
                  :key="workorder.current_handler.id"
                  :label="getUserDisplayName(workorder.current_handler)"
                  :value="workorder.current_handler.id"
              />

              <!-- 显示搜索到的其他用户 -->
              <el-option
                  v-for="user in users.filter(u => !workorder.current_handler || u.id !== workorder.current_handler.id)"
                  :key="user.id"
                  :label="getUserDisplayName(user)"
                  :value="user.id"
              />

              <!-- 如果没有用户数据 -->
              <template v-if="users.length === 0 && !workorder.current_handler">
                <el-option disabled value="">
                  <span style="color: #909399; font-size: 12px;">暂无用户数据，请输入关键词搜索</span>
                </el-option>
              </template>
            </el-select>
            <span v-if="!permission.can_assign" style="color: #909399; font-size: 12px;">无权限修改</span>
          </div>
        </el-descriptions-item>
        <el-descriptions-item label="优先级">
          <el-select
              v-model="workorder.priority"
              size="small"
              @change="handlePriorityChange"
              :disabled="!permission.can_edit"
          >
            <el-option label="低" value="low"/>
            <el-option label="中" value="medium"/>
            <el-option label="高" value="high"/>
            <el-option label="紧急" value="urgent"/>
          </el-select>
        </el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-select
              v-model="workorder.status"
              size="small"
              @change="handleStatusChange"
              :disabled="!permission.can_edit"
          >
            <el-option label="待处理" value="open"/>
            <el-option label="处理中" value="in_progress"/>
            <el-option label="待确认" value="pending"/>
            <el-option label="已解决" value="resolved"/>
            <el-option label="已关闭" value="closed"/>
            <el-option label="已删除" value="deleted"/>
          </el-select>
        </el-descriptions-item>
      </el-descriptions>

      <div class="description-section">
        <h4>问题描述</h4>
        <div class="description-content">{{ workorder.description || '无描述' }}</div>
      </div>

      <!-- 添加工单创建时的文件列表 -->
      <div v-if="workorder.file_list && workorder.file_list.length > 0" class="file_list-section">
        <h4>工单附件</h4>
        <div class="file_list">
          <div v-for="(file, index) in workorder.file_list" :key="index" class="file_list-item">
            <div class="file-info">
              <el-icon :class="getFileIcon(file.type)">
                <component :is="getFileIconComponent(file.type)"/>
              </el-icon>
              <div class="file-details">
                <!-- 修改：为可预览文件添加预览功能 -->
                <div class="file-name-row">
                  <a
                      href="#"
                      @click.prevent="isPreviewableFile(file.type) ? previewFile(file) : downloadWorkOrderFile(file)"
                      class="file-name"
                      :title="file.name"
                      :class="{ 'previewable': isPreviewableFile(file.type) }"
                  >
                    {{ file.name }}
                  </a>
                  <!-- 添加下载按钮 -->
                  <el-button
                      v-if="isPreviewableFile(file.type)"
                      type="text"
                      size="small"
                      @click.stop="downloadWorkOrderFile(file)"
                      class="download-btn"
                  >
                    下载
                  </el-button>
                  <el-button
                      v-else
                      type="text"
                      size="small"
                      @click.stop="downloadWorkOrderFile(file)"
                      class="download-btn"
                  >
                    下载
                  </el-button>
                </div>
                <div class="file-meta">
                  <span class="file-type">{{ getFileTypeText(file.type) }}</span>
                  <span class="file-size">{{ formatFileSize(file.size) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 消息区域 -->
    <el-card class="message-card">
      <template #header>
        <div class="card-header">
          <div class="header-left">
            <span>消息记录 ({{ messages.length }})</span>
            <span v-if="unreadCount > 0" class="unread-count">
        {{ unreadCount }} 条未读
      </span>
          </div>
          <div class="header-right">
            <el-button
                type="text"
                @click="markAllMessagesAsRead"
                :disabled="unreadCount === 0"
            >
              全部标记为已读
            </el-button>
            <el-button type="text" @click="refreshMessages">刷新消息</el-button>
          </div>
        </div>
      </template>

      <!-- 消息列表 -->
      <div class="message-list" ref="messageList">
        <div
            v-for="message in messages"
            :key="message.id"
            :class="['message-item', {
              'own-message': message.sender?.id === currentUser.id,
              'unread': !message.is_read && message.sender?.id !== currentUser.id }]"
            @click="!message.is_read && message.sender?.id !== currentUser.id && markMessageAsRead(message.id)"
        >
          <div class="message-header">
            <span class="sender">{{ message.sender?.username || '系统' }}</span>
            <span class="time">{{ formatDateTime(message.created_at) }}</span>
            <!-- 显示未读标识 -->
            <span v-if="!message.is_read && message.sender?.id !== currentUser.id" class="unread-badge">
              未读
            </span>
          </div>

          <div class="message-content">
            <!-- 文本消息 -->
            <div v-if="message.message_type === 'text'" class="text-message">
              {{ message.content }}
            </div>

            <!-- 系统消息 -->
            <div v-else-if="message.message_type === 'system'" class="system-message">
              <el-icon>
                <InfoFilled/>
              </el-icon>
              <span>{{ message.content }}</span>
            </div>

            <!-- 文件消息 -->
            <div v-else class="file-message">
              <el-icon :class="getFileIcon(message.file_info?.type)">
                <component :is="getFileIconComponent(message.file_info?.type)"/>
              </el-icon>
              <div class="file-info">
                <!-- 修改：为可预览文件添加预览功能 -->
                <div class="file-name-row">
                  <a
                      href="#"
                      @click.prevent="isPreviewableFile(message.file_info?.type) ? previewMessageFile(message) : downloadFile(message)"
                      class="file-name"
                      :class="{ 'previewable': isPreviewableFile(message.file_info?.type) }"
                  >
                    {{ message.file_info?.name || '未知文件' }}
                  </a>
                  <!-- 添加下载按钮 -->
                  <el-button
                      type="text"
                      size="small"
                      @click.stop="downloadFile(message)"
                      class="download-btn"
                  >
                    下载
                  </el-button>
                </div>
                <span class="file-size">{{ formatFileSize(message.file_info?.size) }}</span>
              </div>
              <!-- 缩略图预览（图片） -->
              <div v-if="message.file_info?.type === 'image' && message.file_info?.url"
                   class="thumbnail-container">
                <img
                  :src="message.file_info.url"
                  @click="previewMessageFile(message)"
                  class="thumbnail"
                  :alt="message.file_info.name"
                />
              </div>
              <!-- 视频缩略图或播放按钮 -->
              <div v-if="message.file_info?.type === 'video' && message.file_info?.url"
                   class="video-thumbnail-container">
                <div class="video-thumbnail" @click="previewMessageFile(message)">
                  <el-icon class="play-icon">
                    <VideoPlay />
                  </el-icon>
                  <span class="video-label">点击预览视频</span>
                </div>
              </div>
              <el-button
                  v-if="message.sender?.id === currentUser.id"
                  type="text"
                  size="small"
                  @click="deleteMessage(message.id)"
              >
                删除
              </el-button>
            </div>
          </div>
        </div>

        <div v-if="loadingMessages" class="loading-messages">
          <el-icon class="is-loading">
            <Loading/>
          </el-icon>
          <span>加载消息中...</span>
        </div>

        <div v-if="!loadingMessages && messages.length === 0" class="empty-messages">
          暂无消息
        </div>
      </div>

      <!-- 消息输入 -->
      <div class="message-input" v-if="permission.can_send_message">
        <el-tabs v-model="activeTab">
          <el-tab-pane label="文本" name="text">
            <el-input
                v-model="textMessage"
                type="textarea"
                :rows="3"
                placeholder="输入消息内容..."
                @keydown.enter.prevent="handleTextMessageKeydown"
            />
          </el-tab-pane>
          <el-tab-pane label="文件" name="file">
            <el-upload
                class="file-upload"
                :show-file-list="false"
                :before-upload="handleFileUpload"
                :disabled="uploading"
            >
              <el-button type="primary" :loading="uploading">
                <el-icon>
                  <Upload/>
                </el-icon>
                选择文件
              </el-button>
              <template #tip>
                <div class="el-upload__tip">
                  支持图片、文档、压缩包等格式，最大100MB
                </div>
              </template>
            </el-upload>
          </el-tab-pane>
          <el-tab-pane label="视频" name="video">
            <el-upload
                class="video-upload"
                :show-file-list="false"
                :before-upload="handleVideoUpload"
                accept="video/*"
                :disabled="uploading"
            >
              <el-button type="primary" :loading="uploading">
                <el-icon>
                  <VideoCamera/>
                </el-icon>
                选择视频
              </el-button>
              <template #tip>
                <div class="el-upload__tip">
                  支持MP4、AVI、MOV等视频格式，最大100MB
                </div>
              </template>
            </el-upload>
          </el-tab-pane>
        </el-tabs>

        <div class="input-actions">
          <el-button
              type="primary"
              @click="sendTextMessage"
              :disabled="!textMessage.trim() || sendingMessage"
          >
            发送
          </el-button>
        </div>
      </div>
      <div v-else class="no-permission-message">
        <el-alert
            type="info"
            title="您没有权限在此工单发送消息"
            :closable="false"
            show-icon
        />
      </div>
    </el-card>

    <!-- 编辑对话框 -->
    <el-dialog v-model="editDialogVisible" title="编辑工单" width="600px">
      <el-form :model="editForm" label-width="100px">
        <el-form-item label="标题" required>
          <el-input v-model="editForm.title"/>
        </el-form-item>
        <el-form-item label="问题描述">
          <el-input v-model="editForm.description" type="textarea" :rows="4"/>
        </el-form-item>
        <el-form-item label="优先级">
          <el-select v-model="editForm.priority">
            <el-option label="低" value="low"/>
            <el-option label="中" value="medium"/>
            <el-option label="高" value="high"/>
            <el-option label="紧急" value="urgent"/>
          </el-select>
        </el-form-item>
        <el-form-item label="部署环境">
          <el-select v-model="editForm.deployment_environment_id">
            <el-option
                v-for="env in deployment_environment_id"
                :key="env.id"
                :label="env.name"
                :value="env.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="截止时间">
          <el-date-picker
              v-model="editForm.due_date"
              type="datetime"
              placeholder="选择截止时间"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveEdit" :loading="savingEdit">
            保存
          </el-button>
        </span>
      </template>
    </el-dialog>
    <el-dialog
        v-model="previewDialogVisible"
        :title="previewTitle"
        width="80%"
        :fullscreen="isFullscreen"
        @close="closePreview"
    >
      <!-- 图片预览 -->
      <div v-if="previewType === 'image'" class="image-preview">
        <img :src="previewUrl" :alt="previewFileName" class="preview-image" />
      </div>

      <!-- 视频预览 -->
      <div v-else-if="previewType === 'video'" class="video-preview">
        <video
          :src="previewUrl"
          controls
          autoplay
          class="preview-video"
          :poster="videoPoster"
        >
          您的浏览器不支持视频播放。
        </video>
      </div>

      <!-- 文件信息 -->
      <div class="preview-info" v-if="previewFileName">
        <div class="info-row">
          <span class="label">文件名：</span>
          <span class="value">{{ previewFileName }}</span>
        </div>
        <div class="info-row" v-if="previewFileSize">
          <span class="label">文件大小：</span>
          <span class="value">{{ formatFileSize(previewFileSize) }}</span>
        </div>
        <div class="info-row" v-if="previewFileType">
          <span class="label">文件类型：</span>
          <span class="value">{{ getFileTypeText(previewFileType) }}</span>
        </div>
      </div>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="toggleFullscreen">
            {{ isFullscreen ? '退出全屏' : '全屏' }}
          </el-button>
          <el-button @click="closePreview">关闭</el-button>
          <el-button type="primary" @click="downloadPreviewFile">
            下载文件
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import {computed, nextTick, onMounted, onUnmounted, reactive, ref, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {ElMessage, ElMessageBox, ElNotification} from 'element-plus'
import {
  Document,
  Film,
  Folder,
  InfoFilled,
  Loading,
  Picture,
  Upload,
  VideoCamera,
  VideoPlay
} from '@element-plus/icons-vue'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {useAuthStore} from '@/stores/auth'
import {deleteWorkOrder, getAssignUsers, getWorkOrder, updateWorkOrder} from '@/api/workorder'
import {
  deleteMessage as apiDeleteMessage,
  downloadFile as apiDownloadFile,
  getMessages,
  joinChat,
  markMessageRead as apiMarkMessageRead,
  markMessagesRead as apiMarkMessagesRead,
  sendFileMessage as apiSendFileMessage,
  sendTextMessage as apiSendTextMessage
} from '@/api/message'
import Cookies from "js-cookie";
import {getDeploymentEnvironments} from '@/api/deployment'
import {useAppStore} from "@/stores/app";
import {storeToRefs} from "pinia";
import {chunkedUploadToCos, getOssToken, uploadToCos} from "@/api/upload";

dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const appStore = useAppStore()

// 响应式数据
const workorder = ref({})
const messages = ref([])
const users = ref([])
const loading = ref(false)
const loadingMessages = ref(false)
const sendingMessage = ref(false)
const uploading = ref(false)
const textMessage = ref('')
const activeTab = ref('text')
const editDialogVisible = ref(false)
const savingEdit = ref(false)
const socket = ref(null)
const messageList = ref(null)
const currentHandlerId = ref(null) // 新增：用于单独处理当前处理人的状态
const deployment_environment_id = ref([])

const previewDialogVisible = ref(false)
const previewUrl = ref('')
const previewType = ref('') // 'image' 或 'video'
const previewFileName = ref('')
const previewFileSize = ref(0)
const previewFileType = ref('')
const isFullscreen = ref(false)
const previewTitle = ref('文件预览')
const previewContext = ref(null) // 存储预览上下文（工单附件或消息）
const videoPoster = ref('') // 视频封面图

// 添加 editForm 的声明
const editForm = reactive({
  title: '',
  description: '',
  priority: '',
  due_date: null,
  deployment_environment_id: null
})
// 计算属性
const currentUser = computed(() => authStore.user)
const permission = computed(() => {
  // 直接从 workorder 对象中获取 permissions
  return workorder.value.permissions || {
    can_edit: false,
    can_delete: false,
    can_assign: false,
    can_change_status: false,
    can_send_message: false,
    can_view_history: true
  }
})
const {user} = storeToRefs(authStore)
const {showTabs, systemVersion} = storeToRefs(appStore)
// const debouncedLoadAssignUsers = debounce(loadAssignUsers, 300)

const loadDeploymentEnvironments = async () => {
  try {
    const response = await getDeploymentEnvironments()
    deployment_environment_id.value = response.data || response
  } catch (error) {
    console.error('加载部署环境失败:', error)
  }
}

// 获取用户显示名称的方法
const getUserDisplayName = (user) => {
  if (!user) return '未知用户'
  if (user.username && user.full_name) {
    return `${user.username} - ${user.full_name}`
  }
  return user.username || user.full_name || `用户${user.id}`
}

const getDeploymentEnvironmentName = (id) => {
  const env = deployment_environment_id.value.environments.find(e => e.id === id)
  return env ? env.name : '未知环境'
}

const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}


const statusType = computed(() => {
  const types = {
    open: 'info',
    in_progress: 'warning',
    pending: '',
    resolved: 'success',
    closed: 'danger'
  }
  return types[workorder.value.status] || ''
})

// 监听工单数据变化，更新当前处理人
watch(() => workorder.value.current_handler_id, (newVal) => {
  if (newVal !== undefined && newVal !== null) {
    currentHandlerId.value = newVal
  }
})

// 生命周期
onMounted(async () => {
  // 确保用户数据已加载
  if (!authStore.user && authStore.isAuthenticated) {
    try {
      await authStore.fetchProfile();
    } catch (error) {
      console.error('加载用户信息失败:', error);
      await router.push('/auth/login');
      return;
    }
  }

  // 检查用户是否存在
  if (!authStore.user) {
    await router.push('/auth/login');
    return;
  }

  // 加载部署环境
  await loadDeploymentEnvironments();

  // 先加载工单数据（这里会初始化当前处理人）
  await loadWorkOrder();

  // 再加载分配用户列表（初始为空关键词，会包含当前处理人）
  await loadAssignUsers('');

  // 连接WebSocket
  await connectWebSocket();
});

onUnmounted(() => {
  disconnectWebSocket()
})

// 方法
// 判断文件是否可预览
const isPreviewableFile = (fileType) => {
  return fileType === 'image' || fileType === 'video'
}

// 预览工单附件
const previewFile = (file) => {
  if (!file.url) {
    ElMessage.warning('文件链接无效')
    return
  }

  previewUrl.value = file.url
  previewType.value = file.type
  previewFileName.value = file.name
  previewFileSize.value = file.size
  previewFileType.value = file.type
  previewTitle.value = `预览 - ${file.name}`
  previewContext.value = { type: 'workorder', file }
  previewDialogVisible.value = true

  // 如果是视频，可以设置封面图（如果有的话）
  if (file.type === 'video') {
    // 这里可以设置一个默认的封面图，或者从文件中提取
    // 实际项目中可能需要服务端提供视频截图
    videoPoster.value = ''
  }
}

// 预览消息文件
const previewMessageFile = (message) => {
  const fileUrl = message.cos_url || (message.file_info && message.file_info.url)
  const fileName = message.file_info?.name || message.file_name
  const fileSize = message.file_info?.size
  const fileType = message.file_info?.type

  if (!fileUrl) {
    ElMessage.warning('文件链接无效')
    return
  }

  previewUrl.value = fileUrl
  previewType.value = fileType
  previewFileName.value = fileName
  previewFileSize.value = fileSize
  previewFileType.value = fileType
  previewTitle.value = `预览 - ${fileName}`
  previewContext.value = { type: 'message', message }
  previewDialogVisible.value = true

  // 如果是视频，可以设置封面图
  if (fileType === 'video') {
    videoPoster.value = ''
  }
}

// 下载预览中的文件
const downloadPreviewFile = () => {
  if (!previewContext.value) return

  const { type, file, message } = previewContext.value

  if (type === 'workorder' && file) {
    downloadWorkOrderFile(file)
  } else if (type === 'message' && message) {
    downloadFile(message)
  }
}

// 关闭预览
const closePreview = () => {
  previewDialogVisible.value = false
  previewUrl.value = ''
  previewType.value = ''
  previewFileName.value = ''
  previewFileSize.value = 0
  previewFileType.value = ''
  isFullscreen.value = false
  previewContext.value = null
  videoPoster.value = ''
}

// 切换全屏
const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value
}

const loadWorkOrder = async () => {
  try {
    loading.value = true
    const response = await getWorkOrder(route.params.id)
    workorder.value = response.workorder || response.data?.workorder || {}

    // 调试输出权限信息
    console.log('工单详情权限信息:', workorder.value.permissions)
    console.log('工单当前处理人信息:', workorder.value.current_handler)

    // 初始化当前处理人
    if (workorder.value && workorder.value.current_handler_id) {
      currentHandlerId.value = workorder.value.current_handler_id

      // 重要：将当前处理人添加到用户列表中，以便首次渲染
      if (workorder.value.current_handler) {
        const currentHandler = workorder.value.current_handler
        // 检查是否已经在用户列表中
        const exists = users.value.some(user => user.id === currentHandler.id)
        if (!exists) {
          users.value.unshift(currentHandler)
          console.log('将当前处理人添加到用户列表:', currentHandler.username)
        }
      }
    }

    // 初始化编辑表单
    if (workorder.value) {
      editForm.title = workorder.value.title || ''
      editForm.description = workorder.value.description || ''
      editForm.priority = workorder.value.priority || 'medium'
      editForm.due_date = workorder.value.due_date ? dayjs(workorder.value.due_date).toDate() : null
      editForm.deployment_environment_id = workorder.value.deployment_environment_id
    }

    // 加载消息
    await loadMessages()
  } catch (error) {
    console.error('加载工单失败:', error)
    ElMessage.error(error.response?.data?.message || '加载工单失败')
  } finally {
    loading.value = false
  }
}

const unreadCount = computed(() => {
  return messages.value.filter(
      msg => !msg.is_read && msg.sender?.id !== currentUser.value?.id
  ).length
})

const loadMessages = async () => {
  try {
    loadingMessages.value = true
    const response = await getMessages(route.params.id)
    messages.value = response.messages || response.data?.messages || []

    // 滚动到底部
    scrollToBottom()

    // 自动标记所有未读消息为已读
    await autoMarkAllMessagesRead()

  } catch (error) {
    console.error('加载消息失败:', error)
    ElMessage.error(error.response?.data?.message || '加载消息失败')
  } finally {
    loadingMessages.value = false
  }
}

const loadAssignUsers = async (keyword = '') => {
  try {
    const response = await getAssignUsers({
      username: keyword,
      limit: 10 // 每次只显示前10条
    })

    // 根据API响应结构调整
    if (response && response.data) {
      users.value = response.data.users || []
      console.log('加载用户列表成功:', users.value.length, '个用户')
    } else {
      users.value = response.users || []
    }

    // 确保当前处理人在列表中
    if (currentHandlerId.value && !users.value.find(u => u.id === currentHandlerId.value)) {
      console.warn('当前处理人不在用户列表中:', currentHandlerId.value)
    }
  } catch (error) {
    console.error('加载用户失败:', error)
    // 如果失败，使用空数组而不是错误提示，避免影响用户体验
    users.value = []
  }
}

const debouncedLoadAssignUsers = debounce(loadAssignUsers, 300)

const connectWebSocket = async () => {
  try {
    const response = await joinChat(route.params.id)
    const room = response.room

    // 使用原生 WebSocket 替换 socket.io
    const wsUrl = process.env.VUE_APP_SOCKET_URL
        ? `${process.env.VUE_APP_SOCKET_URL.replace('https', 'wss')}/wsw/${authStore.user?.id}`
        : `wss://${window.location.host}/wsw/${authStore.user?.id}`

    // 添加 token 认证
    const token = Cookies.get('access_token')
    // const url = `${wsUrl}?token=${encodeURIComponent(token || '')}`

    socket.value = new WebSocket(wsUrl)

    socket.value.onopen = () => {
      console.log('WebSocket连接成功')

      // 发送加入房间消息
      socket.value.send(JSON.stringify({
        type: 'join',
        room: room
      }))
    }

    socket.value.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        console.log('收到WebSocket消息:', data)

        switch (data.type) {
          case 'new_message':
            // 确保消息属于当前工单
            if (data.message?.work_order_id === parseInt(route.params.id)) {
              // 将新消息添加到消息列表
              messages.value.push({
                ...data.message,
                // 确保消息格式正确
                sender: data.message.sender || data.user,
                file_info: data.message.file_info || data.fileInfo
              })
              scrollToBottom()

              // 如果是他人发送的消息，显示通知
              if (data.message.sender?.id !== currentUser.value?.id) {
                showNotification({
                  title: '新消息',
                  message: `${data.message.sender?.username || '用户'}: ${data.message.content || '发来一条新消息'}`,
                  type: 'info'
                })
              }
            }
            break

          case 'message_read':
            // 更新消息已读状态
            if (data.messageId) {
              const message = messages.value.find(m => m.id === data.messageId)
              if (message) {
                message.is_read = true
              }
            }
            break

          case 'user_joined':
            console.log('用户加入:', data.user.username)
            ElMessage.info(`${data.user.username} 加入了聊天`)
            break

          case 'user_left':
            console.log('用户离开:', data.user.username)
            ElMessage.info(`${data.user.username} 离开了聊天`)
            break

          case 'connection_established':
            console.log('WebSocket连接已建立')
            // 可以在这里发送加入房间的消息
            if (socket.value && socket.value.readyState === WebSocket.OPEN) {
              socket.value.send(JSON.stringify({
                type: 'join',
                room: `workorder_${route.params.id}`,
                userId: currentUser.value.id,
                username: currentUser.value.username,
                timestamp: new Date().toISOString()
              }))
            }
            break

          case 'file_upload':
            // 处理文件上传通知
            if (data.file_info && data.room === `workorder_${route.params.id}`) {
              console.log('收到文件上传通知:', data.file_info.name)
            }
            break
        }
      } catch (error) {
        console.error('解析WebSocket消息失败:', error)
      }
    }

    socket.value.onclose = (event) => {
      console.log('WebSocket断开连接:', event.code, event.reason)
      // 5秒后重连
      setTimeout(() => {
        if (!socket.value || socket.value.readyState === WebSocket.CLOSED) {
          connectWebSocket()
        }
      }, 5000)
    }

    socket.value.onerror = (error) => {
      console.error('WebSocket错误:', error)
    }

  } catch (error) {
    console.error('连接WebSocket失败:', error)
  }
}

const showNotification = (options) => {
  const {title, message, type = 'info'} = options
  ElNotification({
    title,
    message,
    type,
    duration: 3000,
    position: 'bottom-right'
  })
}

const disconnectWebSocket = () => {
  if (socket.value) {
    socket.value.disconnect()
    socket.value = null
  }
}

const refreshData = () => {
  loadWorkOrder()
}

const refreshMessages = () => {
  loadMessages()
}

const handleEdit = () => {
  if (!permission.value.can_edit) {
    ElMessage.warning('您没有权限编辑此工单')
    return
  }
  editDialogVisible.value = true
}

const handleDelete = async () => {
  if (!permission.value.can_delete) {
    ElMessage.warning('您没有权限删除此工单')
    return
  }
  try {
    await ElMessageBox.confirm(
        '确定要删除此工单吗？删除后无法恢复。',
        '确认删除',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
    )

    await deleteWorkOrder(route.params.id)
    ElMessage.success('工单删除成功')
    await router.push('/workorders')
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.error || '删除失败')
    }
  }
}

// 修复当前处理人更改方法
const handleAssigneeChange = async (handlerId) => {
  if (!permission.value.can_assign) {
    ElMessage.warning('您没有权限修改处理人')
    return
  }

  try {
    await updateWorkOrder(route.params.id, {
      current_handler_id: handlerId
    })

    // 更新本地数据
    workorder.value.current_handler_id = handlerId
    currentHandlerId.value = handlerId

    ElMessage.success('处理人更新成功')
  } catch (error) {
    console.error('更新处理人失败:', error)
    ElMessage.error(error.response?.data?.error || '更新失败')
    // 恢复原来的值
    currentHandlerId.value = workorder.value.current_handler_id
  }
}

const handlePriorityChange = async (priority) => {
  if (!permission.value.can_edit) {
    ElMessage.warning('您没有权限修改优先级')
    return
  }
  try {
    await updateWorkOrder(route.params.id, {priority})
    ElMessage.success('优先级更新成功')
  } catch (error) {
    ElMessage.error(error.response?.data?.error || '更新失败')
  }
}

const handleStatusChange = async (status) => {
  if (!permission.value.can_edit) {
    ElMessage.warning('您没有权限修改状态')
    return
  }
  try {
    await updateWorkOrder(route.params.id, {status})
    ElMessage.success('状态更新成功')
  } catch (error) {
    ElMessage.error(error.response?.data?.error || '更新失败')
  }
}

const saveEdit = async () => {
  try {
    savingEdit.value = true

    const data = {...editForm}
    if (data.due_date) {
      data.due_date = dayjs(data.due_date).format('YYYY-MM-DD HH:mm:ss')
    }

    await updateWorkOrder(route.params.id, data)

    editDialogVisible.value = false
    ElMessage.success('工单更新成功')
    await loadWorkOrder()
  } catch (error) {
    ElMessage.error(error.response?.data?.error || '更新失败')
  } finally {
    savingEdit.value = false
  }
}

// 添加工单文件下载方法
const downloadWorkOrderFile = async (file) => {
  try {
    if (file.url) {
      // 直接从COS URL下载
      const link = document.createElement('a')
      link.href = file.url
      link.setAttribute('download', file.name || 'file')
      link.setAttribute('target', '_blank')
      document.body.appendChild(link)
      link.click()
      link.remove()

      ElMessage.success(`开始下载: ${file.name}`)
    } else {
      ElMessage.error('文件链接无效')
    }
  } catch (error) {
    console.error('下载文件失败:', error)
    ElMessage.error(`下载文件失败: ${error.message}`)
  }
}


// 获取文件类型文本
const getFileTypeText = (fileType) => {
  const typeMap = {
    'pdf': 'PDF文档',
    'image': '图片',
    'video': '视频',
    'document': '文档',
    'spreadsheet': '表格',
    'archive': '压缩包',
    'other': '其他文件'
  }
  return typeMap[fileType] || '文件'
}

const sendTextMessage = async () => {
  if (!permission.value.can_send_message) {
    ElMessage.warning('您没有权限在此工单发送消息')
    return
  }

  if (!textMessage.value.trim()) return

  try {
    sendingMessage.value = true

    // 1. 首先通过API发送文本消息（用于消息落库）
    const apiResponse = await apiSendTextMessage(route.params.id, textMessage.value)

    // 2. 如果API成功，通过WebSocket广播消息
    if (socket.value && socket.value.readyState === WebSocket.OPEN && apiResponse.data) {
      const messageData = apiResponse.data.data || apiResponse.data

      socket.value.send(JSON.stringify({
        type: 'message',
        room: `workorder_${route.params.id}`,
        message: textMessage.value.trim(),
        messageId: messageData.id, // 传递消息ID用于标识
        messageType: 'text',
        timestamp: new Date().toISOString(),
        work_order_id: parseInt(route.params.id),
        sender: {
          id: currentUser.value.id,
          username: currentUser.value.username,
          full_name: currentUser.value.full_name
        }
      }))
    }

    textMessage.value = ''
    ElMessage.success('消息发送成功')

    // 3. 重新加载消息列表
    await loadMessages()

  } catch (error) {
    ElMessage.error(error.response?.data?.error || '发送失败')
  } finally {
    sendingMessage.value = false
  }
}

const markMessageAsRead = async (messageId) => {
  try {
    // 只标记他人发送且未读的消息
    const message = messages.value.find(m => m.id === messageId)
    if (!message || message.is_read || message.sender?.id === currentUser.value?.id) {
      return
    }

    // 调用API标记已读
    await apiMarkMessageRead(messageId)

    // 更新本地状态
    message.is_read = true

    // 发送WebSocket通知（可选，用于实时同步）
    if (socket.value && socket.value.readyState === WebSocket.OPEN) {
      socket.value.send(JSON.stringify({
        type: 'message_read',
        room: `workorder_${route.params.id}`,
        messageId: messageId,
        userId: currentUser.value.id,
        timestamp: new Date().toISOString()
      }))
    }

    console.log('消息已标记为已读:', messageId)
  } catch (error) {
    console.error('标记消息已读失败:', error)
  }
}

// 批量标记所有未读消息为已读
const markAllMessagesAsRead = async () => {
  try {
    // 获取他人发送的未读消息
    const unreadMessageIds = messages.value
        .filter(msg => !msg.is_read && msg.sender?.id !== currentUser.value?.id)
        .map(msg => msg.id)

    if (unreadMessageIds.length === 0) return

    // 调用批量标记API
    await apiMarkMessagesRead(unreadMessageIds)

    // 更新本地所有消息状态
    messages.value.forEach(msg => {
      if (unreadMessageIds.includes(msg.id)) {
        msg.is_read = true
      }
    })

    ElMessage.success(`已标记 ${unreadMessageIds.length} 条消息为已读`)
  } catch (error) {
    console.error('批量标记消息已读失败:', error)
    ElMessage.error('标记已读失败')
  }
}

// 进入工单页面时自动标记所有未读消息为已读
const autoMarkAllMessagesRead = async () => {
  try {
    const unreadMessageIds = messages.value
        .filter(msg => !msg.is_read && msg.sender?.id !== currentUser.value?.id)
        .map(msg => msg.id)

    if (unreadMessageIds.length > 0) {
      await apiMarkMessagesRead(unreadMessageIds)

      // 更新本地状态
      messages.value.forEach(msg => {
        if (unreadMessageIds.includes(msg.id)) {
          msg.is_read = true
        }
      })

      console.log(`自动标记了 ${unreadMessageIds.length} 条未读消息`)
    }
  } catch (error) {
    console.error('自动标记已读失败:', error)
  }
}

const handleTextMessageKeydown = (event) => {
  if (event.ctrlKey && event.key === 'Enter') {
    sendTextMessage()
  }
}

const handleFileUpload = async (file, messageType = 'file') => {
  try {
    uploading.value = true

    // 1. 上传文件到COS
    const tokenResponse = await getOssToken()
    const ossConfig = tokenResponse.data || tokenResponse

    const timestamp = Date.now()
    const randomStr = Math.random().toString(36).substring(2, 8)
    const extension = file.name.split('.').pop()
    const cosPath = `workorder_messages/${route.params.id}/${timestamp}_${randomStr}.${extension}`

    let fileUrl
    if (file.size > 100 * 1024 * 1024) {
      fileUrl = await chunkedUploadToCos(file, ossConfig, cosPath)
    } else {
      fileUrl = await uploadToCos(file, ossConfig, cosPath)
    }

    // 2. 通过API发送文件消息（包含COS信息）
    const formData = new FormData()
    formData.append('message_type', messageType)
    formData.append('content', `文件: ${file.name}`)
    formData.append('cos_url', fileUrl)
    formData.append('cos_key', cosPath)
    formData.append('file_name', file.name)
    formData.append('file_size', file.size)
    formData.append('file_type', getFileTypeCategory(file.name, file.type))

    const apiResponse = await apiSendFileMessage(route.params.id, formData, messageType)

    // 3. 通过WebSocket广播消息 - 统一消息体格式
    if (socket.value && socket.value.readyState === WebSocket.OPEN && apiResponse.data) {
      const messageData = apiResponse.data.data || apiResponse.data

      socket.value.send(JSON.stringify({
        type: 'message',
        room: `workorder_${route.params.id}`,
        message: `文件: ${file.name}`, // 统一使用 message 字段
        messageId: messageData.id,
        messageType: messageType,
        fileInfo: {
          name: file.name,
          size: file.size,
          type: getFileTypeCategory(file.name, file.type),
          url: fileUrl,
          cos_key: cosPath
        },
        timestamp: new Date().toISOString(),
        work_order_id: parseInt(route.params.id),
        sender: {
          id: currentUser.value.id,
          username: currentUser.value.username,
          full_name: currentUser.value.full_name
        }
      }))
    }

    ElMessage.success(`${file.name} 上传成功`)

    // 4. 重新加载消息列表
    await loadMessages()

  } catch (error) {
    console.error('文件上传失败:', error)
    ElMessage.error(error.response?.data?.error || '上传失败')
  } finally {
    uploading.value = false
  }

  return false // 阻止默认上传行为
}

const handleVideoUpload = async (file) => {
  return handleFileUpload(file, 'video')
}

const deleteMessage = async (messageId) => {
  try {
    await ElMessageBox.confirm(
        '确定要删除此消息吗？',
        '确认删除',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
    )

    await apiDeleteMessage(messageId)
    messages.value = messages.value.filter(msg => msg.id !== messageId)
    ElMessage.success('消息删除成功')
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.error || '删除失败')
    }
  }
}

const downloadFile = async (message) => {
  try {
    // 如果是COS文件，直接重定向到COS URL
    const fileUrl = message.cos_url || (message.file_info && message.file_info.url)
    const fileName = message.file_info?.name || message.file_name

    if (fileUrl) {
      // 创建隐藏的a标签进行下载
      const link = document.createElement('a')
      link.href = fileUrl
      link.setAttribute('download', fileName || 'file')
      link.setAttribute('target', '_blank')
      document.body.appendChild(link)
      link.click()
      link.remove()

      ElMessage.success(`开始下载: ${fileName}`)
    } else {
      // 如果是旧版本地文件，使用API下载
      const response = await apiDownloadFile(message.id)
      const url = window.URL.createObjectURL(new Blob([response]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName || 'file')
      document.body.appendChild(link)
      link.click()
      link.remove()
    }
  } catch (error) {
    console.error('下载失败:', error)
    ElMessage.error(error.response?.data?.error || '下载失败')
  }
}

const getFileTypeCategory = (fileName, fileType = '') => {
  if (!fileName) return 'other'

  const name = fileName.toLowerCase()
  const type = fileType.toLowerCase()

  // 图片类型（扩展列表）
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.tiff', '.tif', '.ico']
  const imageTypes = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
    'image/tiff', 'image/x-icon', 'image/bmp'
  ]

  // 视频类型（扩展列表）
  const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.webm', '.m4v', '.mpg', '.mpeg', '.3gp']
  const videoTypes = [
    'video/mp4', 'video/avi', 'video/quicktime', 'video/x-ms-wmv', 'video/x-flv',
    'video/x-matroska', 'video/webm', 'video/3gpp'
  ]

  // 文档类型
  const documentExtensions = ['.doc', '.docx', '.txt', '.pdf', '.ppt', '.pptx', '.xls', '.xlsx', '.rtf', '.odt']
  const documentTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'application/pdf',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ]

  // 压缩包类型
  const archiveExtensions = ['.zip', '.rar', '.7z', '.tar', '.gz', '.bz2', '.xz']
  const archiveTypes = [
    'application/zip',
    'application/x-rar-compressed',
    'application/x-7z-compressed',
    'application/x-tar',
    'application/gzip'
  ]

  // 检查扩展名
  const extension = name.substring(name.lastIndexOf('.')).toLowerCase()

  // 首先检查文件类型
  if (imageTypes.includes(type)) {
    return 'image'
  }
  if (videoTypes.includes(type)) {
    return 'video'
  }

  // 然后检查扩展名
  if (imageExtensions.includes(extension)) {
    return 'image'
  }
  if (videoExtensions.includes(extension)) {
    return 'video'
  }
  if (documentExtensions.includes(extension) || documentTypes.includes(type)) {
    // 区分 PDF 和其他文档
    if (extension === '.pdf' || type === 'application/pdf') {
      return 'pdf'
    }
    return 'document'
  }
  if (archiveExtensions.includes(extension) || archiveTypes.includes(type)) {
    return 'archive'
  }
  if (extension === '.xlsx' || extension === '.xls' || type.includes('spreadsheet') || type.includes('excel')) {
    return 'spreadsheet'
  }

  return 'other'
}

const getFileIcon = (fileType) => {
  const icons = {
    image: 'file-image',
    video: 'file-video',
    document: 'file-document',
    archive: 'file-archive',
    pdf: 'file-document',
    spreadsheet: 'file-document',
    other: 'file'
  }
  return icons[fileType] || 'file'
}

const getFileIconComponent = (fileType) => {
  const components = {
    image: Picture,
    video: Film,
    document: Document,
    archive: Folder,
    pdf: Document,
    spreadsheet: Document,
    other: Document
  }
  return components[fileType] || Document
}

const formatDate = (date) => {
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
}

const formatDateTime = (date) => {
  return dayjs(date).format('MM-DD HH:mm')
}

const formatTimeSpent = (seconds) => {
  if (!seconds) return '0秒'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours > 0) {
    return `${hours}小时${minutes}分`
  } else if (minutes > 0) {
    return `${minutes}分${secs}秒`
  } else {
    return `${secs}秒`
  }
}

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B'

  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messageList.value) {
      messageList.value.scrollTop = messageList.value.scrollHeight
    }
  })
}
</script>

<style scoped lang="scss">
/* 添加样式 */
.no-permission-message {
  margin-top: 20px;
}

.workorder-detail {
  padding: 20px;

  .file_list-section, .file-message {
    .file-info {
      .file-details {
        .file-name-row {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .file-name {
            flex: 1;
            margin-right: 10px;
            color: #409eff;
            text-decoration: none;
            cursor: pointer;
            word-break: break-word;

            &:hover {
              text-decoration: underline;
            }

            &.previewable {
              color: #67c23a;
              font-weight: 500;

              &:hover {
                color: #85ce61;
              }
            }
          }

          .download-btn {
            flex-shrink: 0;
            padding: 0;
            height: auto;
          }
        }

        .file-meta {
          margin-top: 4px;
        }
      }
    }
  }

  /* 缩略图样式 */
  .thumbnail-container {
    margin-top: 8px;

    .thumbnail {
      max-width: 150px;
      max-height: 150px;
      border-radius: 4px;
      cursor: pointer;
      border: 1px solid #ebeef5;
      transition: all 0.3s;

      &:hover {
        border-color: #409eff;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      }
    }
  }

  /* 视频缩略图样式 */
  .video-thumbnail-container {
    margin-top: 8px;

    .video-thumbnail {
      width: 150px;
      height: 100px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      border: 1px solid #ebeef5;
      transition: all 0.3s;

      &:hover {
        border-color: #409eff;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      }

      .play-icon {
        font-size: 32px;
        color: white;
        margin-bottom: 8px;
      }

      .video-label {
        color: white;
        font-size: 12px;
      }
    }
  }

  /* 预览对话框样式 */
  .image-preview, .video-preview {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    max-height: 70vh;
    overflow: hidden;

    .preview-image {
      max-width: 100%;
      max-height: 70vh;
      object-fit: contain;
    }

    .preview-video {
      max-width: 100%;
      max-height: 70vh;
    }
  }

  .preview-info {
    margin-top: 20px;
    padding: 15px;
    background: #f5f7fa;
    border-radius: 4px;

    .info-row {
      display: flex;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }

      .label {
        width: 80px;
        color: #909399;
        font-size: 14px;
      }

      .value {
        flex: 1;
        color: #606266;
        font-size: 14px;
        word-break: break-all;
      }
    }
  }

  /* 响应式调整 */
  @media (max-width: 768px) {
    .thumbnail-container .thumbnail {
      max-width: 120px;
      max-height: 120px;
    }

    .video-thumbnail-container .video-thumbnail {
      width: 120px;
      height: 80px;
    }
  }

  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .header-actions {
      .el-button + .el-button {
        margin-left: 10px;
      }
    }
  }

  .info-card, .message-card {
    margin-bottom: 20px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .header-left {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .header-right {
        display: flex;
        gap: 10px;
      }
    }
  }

  .description-section {
    margin-top: 20px;

    h4 {
      margin: 0 0 10px 0;
      color: #606266;
    }

    .description-content {
      padding: 10px;
      background: #f5f7fa;
      border-radius: 4px;
      white-space: pre-wrap;
      line-height: 1.6;
    }
  }

  .message-list {
    height: 400px;
    overflow-y: auto;
    padding: 10px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    margin-bottom: 20px;

    .message-item {
      margin-bottom: 15px;
      padding: 10px;
      border-radius: 4px;
      background: #f5f7fa;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background: rgba(64, 158, 255, 0.05);
      }

      &.unread {
        background: #f0f9ff;
        border-left: 3px solid #409eff;

        .message-header {
          display: flex;
          align-items: center;
          gap: 8px;

          .unread-count {
            color: #f56c6c;
            font-size: 12px;
            margin-left: 8px;
          }
        }
      }

      &.own-message {
        background: #ecf5ff;

        .message-header {
          color: #409eff;
        }
      }

      .message-header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 5px;
        font-size: 12px;
        color: #909399;

        .sender {
          font-weight: bold;
        }
      }

      .message-content {
        .text-message {
          line-height: 1.5;
          word-wrap: break-word;
        }

        .system-message {
          display: flex;
          align-items: center;
          color: #e6a23c;
          font-size: 14px;

          .el-icon {
            margin-right: 5px;
          }
        }

        .file-message {
          display: flex;
          align-items: center;
          padding: 8px;
          background: white;
          border: 1px solid #dcdfe6;
          border-radius: 4px;

          .el-icon {
            margin-right: 10px;
            font-size: 24px;
            color: #409eff;
          }

          .file-info {
            flex: 1;

            .file-name {
              display: block;
              color: #409eff;
              text-decoration: none;

              &:hover {
                text-decoration: underline;
              }
            }

            .file-size {
              font-size: 12px;
              color: #909399;
            }
          }
        }
      }
    }

    .loading-messages, .empty-messages {
      text-align: center;
      padding: 40px;
      color: #909399;

      .el-icon {
        margin-right: 10px;
        animation: rotating 2s linear infinite;
      }
    }
  }

  .message-input {
    .el-tabs {
      :deep(.el-tabs__header) {
        margin-bottom: 10px;
      }
    }

    .file-upload, .video-upload {
      .el-upload__tip {
        margin-top: 10px;
        font-size: 12px;
        color: #909399;
      }
    }

    .input-actions {
      margin-top: 15px;
      text-align: right;
    }
  }
}

/* 对话框全屏样式 */
:deep(.el-dialog__wrapper) {
  .el-dialog {
    &.is-fullscreen {
      .el-dialog__body {
        max-height: calc(100vh - 120px);
      }
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>